import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  AlertTriangleIcon,
  UserIcon,
  FileTextIcon,
  CalendarIcon,
  ClockIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ShieldIcon,
  AlertCircleIcon,
  X
} from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

interface Allergy {
  id: string;
  patientId: string;
  allergen: string;
  allergenType: 'DRUG' | 'FOOD' | 'ENVIRONMENTAL' | 'LATEX' | 'OTHER';
  severity: 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING';
  reaction: string;
  status: 'ACTIVE' | 'INACTIVE' | 'RESOLVED';
  notes?: string;
  patient: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface Patient {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const AllergiesPage: React.FC = () => {
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAllergy, setSelectedAllergy] = useState<Allergy | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);

  useEffect(() => {
    fetchAllergies();
    fetchAnalytics();
    fetchAlerts();
    fetchPatients();
  }, []);

  const fetchAllergies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/allergies');
      setAllergies(response.data.allergies || []);
    } catch (error) {
      console.error('Error fetching allergies:', error);
      toast.error('Failed to fetch allergies');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/allergies/analytics/overview');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/allergies/alerts/all');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
    setSelectedAllergy(null);
  };

  const handleViewAllergy = (allergy: Allergy) => {
    setSelectedAllergy(allergy);
    setShowViewModal(true);
  };

  const handleEditAllergy = (allergy: Allergy) => {
    setSelectedAllergy(allergy);
    setShowEditModal(true);
  };

  const handleDeleteAllergy = async (allergyId: string) => {
    if (window.confirm('Are you sure you want to delete this allergy?')) {
      try {
        await api.delete(`/allergies/${allergyId}`);
        toast.success('Allergy deleted successfully');
        fetchAllergies();
      } catch (error) {
        console.error('Error deleting allergy:', error);
        toast.error('Failed to delete allergy');
      }
    }
  };

  const filteredAllergies = allergies.filter(allergy => {
    const matchesSearch = 
      allergy.allergen.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allergy.reaction.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allergy.patient.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allergy.patient.user.lastName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeverity = !severityFilter || allergy.severity === severityFilter;
    const matchesType = !typeFilter || allergy.allergenType === typeFilter;
    const matchesStatus = !statusFilter || allergy.status === statusFilter;
    
    return matchesSearch && matchesSeverity && matchesType && matchesStatus;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'LIFE_THREATENING': return 'bg-red-100 text-red-800';
      case 'SEVERE': return 'bg-orange-100 text-orange-800';
      case 'MODERATE': return 'bg-yellow-100 text-yellow-800';
      case 'MILD': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'DRUG': return 'bg-red-100 text-red-800';
      case 'FOOD': return 'bg-blue-100 text-blue-800';
      case 'ENVIRONMENTAL': return 'bg-green-100 text-green-800';
      case 'LATEX': return 'bg-purple-100 text-purple-800';
      case 'OTHER': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-red-100 text-red-800';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800';
      case 'RESOLVED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Allergies</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage patient allergies and safety alerts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Allergy
        </button>
      </div>

      {/* Safety Alerts */}
      {alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Safety Alerts ({alerts.length})
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>There are active allergy alerts that require attention.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Allergies</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ShieldIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.active}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Severe</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.bySeverity?.find((s: any) => s.severity === 'SEVERE')?._count?.severity || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Drug Allergies</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.byType?.find((t: any) => t.allergenType === 'DRUG')?._count?.allergenType || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search allergies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Severities</option>
            <option value="LIFE_THREATENING">Life Threatening</option>
            <option value="SEVERE">Severe</option>
            <option value="MODERATE">Moderate</option>
            <option value="MILD">Mild</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            <option value="DRUG">Drug</option>
            <option value="FOOD">Food</option>
            <option value="ENVIRONMENTAL">Environmental</option>
            <option value="LATEX">Latex</option>
            <option value="OTHER">Other</option>
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="RESOLVED">Resolved</option>
          </select>
        </div>
      </div>

      {/* Allergies Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allergen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reaction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAllergies.map((allergy) => (
                <tr key={allergy.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {allergy.patient.user.firstName} {allergy.patient.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {allergy.patient.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{allergy.allergen}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(allergy.allergenType)}`}>
                      {allergy.allergenType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(allergy.severity)}`}>
                      {allergy.severity.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(allergy.status)}`}>
                      {allergy.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 truncate max-w-xs">
                      {allergy.reaction}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900 mr-3" onClick={() => handleViewAllergy(allergy)}>
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button className="text-primary-600 hover:text-primary-900 mr-3" onClick={() => handleEditAllergy(allergy)}>
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900" onClick={() => handleDeleteAllergy(allergy.id)}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredAllergies.length === 0 && (
          <div className="text-center py-12">
            <AlertTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No allergies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a new allergy record.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Allergy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Allergy Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Allergy</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const allergyData = {
                  patientId: formData.get('patientId') as string,
                  allergen: formData.get('allergen') as string,
                  allergenType: formData.get('allergenType') as 'DRUG' | 'FOOD' | 'ENVIRONMENTAL' | 'LATEX' | 'OTHER',
                  severity: formData.get('severity') as 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING',
                  reaction: formData.get('reaction') as string,
                  notes: formData.get('notes') as string || undefined,
                  status: 'ACTIVE' as const
                };

                try {
                  await api.post('/allergies', allergyData);
                  toast.success('Allergy added successfully');
                  handleCloseModal();
                  fetchAllergies();
                } catch (error) {
                  console.error('Error adding allergy:', error);
                  toast.error('Failed to add allergy');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Allergen */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergen *
                    </label>
                    <input
                      type="text"
                      name="allergen"
                      required
                      placeholder="e.g., Penicillin, Peanuts, Latex"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Allergen Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergen Type *
                    </label>
                    <select
                      name="allergenType"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select type</option>
                      <option value="DRUG">Drug</option>
                      <option value="FOOD">Food</option>
                      <option value="ENVIRONMENTAL">Environmental</option>
                      <option value="LATEX">Latex</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Severity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Severity *
                    </label>
                    <select
                      name="severity"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select severity</option>
                      <option value="MILD">Mild</option>
                      <option value="MODERATE">Moderate</option>
                      <option value="SEVERE">Severe</option>
                      <option value="LIFE_THREATENING">Life Threatening</option>
                    </select>
                  </div>
                </div>

                {/* Reaction */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reaction *
                  </label>
                  <textarea
                    name="reaction"
                    required
                    rows={3}
                    placeholder="Describe the allergic reaction (e.g., rash, hives, difficulty breathing, anaphylaxis)..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    placeholder="Any additional information about the allergy..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Add Allergy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Allergy Modal */}
      {showViewModal && selectedAllergy && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">View Allergy</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedAllergy.patient.user.firstName} {selectedAllergy.patient.user.lastName}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allergen
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedAllergy.allergen}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allergen Type
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedAllergy.allergenType}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Severity
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedAllergy.severity}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedAllergy.status}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reaction
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedAllergy.reaction}
                  </div>
                </div>
                {selectedAllergy.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <div className="text-sm text-gray-900">
                      {selectedAllergy.notes}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Created
                  </label>
                  <div className="text-sm text-gray-900">
                    {new Date(selectedAllergy.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Allergy Modal */}
      {showEditModal && selectedAllergy && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Allergy</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const allergyData = {
                  patientId: formData.get('patientId') as string,
                  allergen: formData.get('allergen') as string,
                  allergenType: formData.get('allergenType') as 'DRUG' | 'FOOD' | 'ENVIRONMENTAL' | 'LATEX' | 'OTHER',
                  severity: formData.get('severity') as 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING',
                  reaction: formData.get('reaction') as string,
                  notes: formData.get('notes') as string || undefined,
                  status: formData.get('status') as 'ACTIVE' | 'INACTIVE' | 'RESOLVED'
                };

                try {
                  await api.put(`/allergies/${selectedAllergy.id}`, allergyData);
                  toast.success('Allergy updated successfully');
                  handleCloseModal();
                  fetchAllergies();
                } catch (error) {
                  console.error('Error updating allergy:', error);
                  toast.error('Failed to update allergy');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      defaultValue={selectedAllergy.patientId}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Allergen */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergen *
                    </label>
                    <input
                      type="text"
                      name="allergen"
                      required
                      defaultValue={selectedAllergy.allergen}
                      placeholder="e.g., Penicillin, Peanuts, Latex"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Allergen Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergen Type *
                    </label>
                    <select
                      name="allergenType"
                      required
                      defaultValue={selectedAllergy.allergenType}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select type</option>
                      <option value="DRUG">Drug</option>
                      <option value="FOOD">Food</option>
                      <option value="ENVIRONMENTAL">Environmental</option>
                      <option value="LATEX">Latex</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* Severity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Severity *
                    </label>
                    <select
                      name="severity"
                      required
                      defaultValue={selectedAllergy.severity}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select severity</option>
                      <option value="MILD">Mild</option>
                      <option value="MODERATE">Moderate</option>
                      <option value="SEVERE">Severe</option>
                      <option value="LIFE_THREATENING">Life Threatening</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      name="status"
                      required
                      defaultValue={selectedAllergy.status}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="RESOLVED">Resolved</option>
                    </select>
                  </div>
                </div>

                {/* Reaction */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reaction *
                  </label>
                  <textarea
                    name="reaction"
                    required
                    rows={3}
                    defaultValue={selectedAllergy.reaction}
                    placeholder="Describe the allergic reaction (e.g., rash, hives, difficulty breathing, anaphylaxis)..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    defaultValue={selectedAllergy.notes || ''}
                    placeholder="Any additional information about the allergy..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Update Allergy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllergiesPage; 